import React from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

// The app's date picker: MUI's desktop picker with the violet selected day
// and the rounded, violet-focused field the other pickers use.
//
// `value` and `onChange` speak ISO dates ("YYYY-MM-DD"). MUI emits
// intermediate, invalid values while a date is typed section by section; the
// field keeps those as a local draft so typing is not snapped back, and only
// a complete, valid date inside the allowed range reaches `onChange`.
const DAY_SX = { "&.MuiPickersDay-root.Mui-selected": { backgroundColor: "#6d28d9", "&:hover, &:focus": { backgroundColor: "#5b21b6" } } };
const fieldSx = (height, width) => ({
  ...(width ? { width } : {}),
  "& .MuiOutlinedInput-root": { height, backgroundColor: "white", borderRadius: "8px", "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" } },
});
const parseIso = (iso) => (iso ? dayjs(iso) : null);

export default function RightworkDatePicker({ value, onChange, label, minDate, fullWidth = false, height = 42, width, disabled = false }) {
  const [draft, setDraft] = React.useState(() => parseIso(value));
  const invalid = React.useRef(false);
  React.useEffect(() => { setDraft(parseIso(value)); invalid.current = false; }, [value]);
  // Editing that ends on an incomplete or out-of-range value goes back to
  // the committed one, so the field never shows a value the form is not
  // using while its actions stay enabled.
  const settle = () => {
    if (invalid.current) {
      setDraft(parseIso(value));
      invalid.current = false;
    }
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DesktopDatePicker
        value={draft}
        disabled={disabled}
        minDate={minDate ? dayjs(minDate) : undefined}
        onChange={(picked, context) => {
          setDraft(picked);
          const complete = Boolean(picked && picked.isValid() && !context?.validationError);
          invalid.current = !complete;
          if (complete) onChange(picked.format("YYYY-MM-DD"));
        }}
        onClose={settle}
        slotProps={{ day: { sx: DAY_SX }, textField: { fullWidth, size: "small", sx: fieldSx(height, width), inputProps: { "aria-label": label }, onBlur: settle } }}
      />
    </LocalizationProvider>
  );
}
